import {useState} from 'react';
import {downloadText} from '../../lib/browserFiles';
import {Conversion} from '../../domain/types';
import {useAppDispatch} from '../../store/hooks';
import {downloadConversionXml} from '../../store/slices/conversionsThunks';
import {useToast} from '../Toast';
import {stripTimestamp} from '../../utils/filenameUtils';

interface ConversionHistoryProps {
  conversions: Conversion[];
  showConversions?: boolean; // Kept for backward compatibility but unused
  onToggle?: () => void; // Kept for backward compatibility but unused
  onDelete: (conversionId: string) => Promise<void>;
}

export const ConversionHistory = ({
  conversions,
  onDelete,
}: ConversionHistoryProps) => {
  const dispatch = useAppDispatch();
  const {showToast} = useToast();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  if (conversions.length === 0) {
    return null;
  }

  const handleDownloadXml = async (conversion: Conversion) => {
    try {
      setDownloadingId(conversion.id);

      // Download the XML content using the new authenticated endpoint
      const xmlContent = await dispatch(downloadConversionXml({conversionId: conversion.id})).unwrap();

      const filenameWithTimestamp = conversion.xmlR2Key.split('/').pop() || 'conversion.xml';
      const filename = stripTimestamp(filenameWithTimestamp);
      downloadText(xmlContent, filename, 'application/xml');

      showToast(`Downloaded ${filename}`, 'success');
    } catch (error) {
      console.error('Failed to download XML:', error);
      showToast(`Failed to download XML: ${error}`, 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteClick = (conversionId: string) => {
    setDeleteConfirmId(conversionId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;

    try {
      setIsDeleting(true);
      await onDelete(deleteConfirmId);
      showToast('Conversion deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete conversion:', error);
      showToast(`Failed to delete conversion: ${error}`, 'error');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold text-white mb-3">Recent Conversions ({conversions.length})</h3>

      {conversions.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {conversions.map(conversion => (
            <div
              key={conversion.id}
              className="p-4 bg-gray-800 border border-gray-700 rounded-lg hover:border-gray-600 transition-colors"
            >
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white font-medium text-sm truncate">
                      {conversion.xmlPath.split(/[\\/]/).pop()}
                    </span>
                    <span className="px-2 py-0.5 bg-green-600/20 text-green-400 text-xs rounded border border-green-600/30">
                      {conversion.mode}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="text-xs">
                      <span className="text-gray-400">Censored Words:</span>
                      <span className="text-red-400 font-semibold ml-1">{conversion.censoredWords}</span>
                    </div>
                    <div className="text-xs">
                      <span className="text-gray-400">Total Cuts:</span>
                      <span className="text-yellow-400 font-semibold ml-1">{conversion.totalCuts}</span>
                    </div>
                    <div className="text-xs col-span-2">
                      <span className="text-gray-400">Markers:</span>
                      <span className="text-blue-400 ml-1">{conversion.markers}</span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 truncate" title={conversion.xmlPath}>
                    📁 {conversion.xmlPath}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    🕒 {new Date(conversion.createdAt).toLocaleString()}
                  </div>
                </div>

                <div className="flex gap-1">
                  <button
                    onClick={() => handleDownloadXml(conversion)}
                    disabled={downloadingId === conversion.id}
                    className="px-2 py-1 bg-blue-600/20 text-blue-400 text-xs rounded hover:bg-blue-600/30 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Download XML"
                  >
                    {downloadingId === conversion.id ? '⏳' : '💾'}
                  </button>
                  <button
                    onClick={() => handleDeleteClick(conversion.id)}
                    className="px-2 py-1 bg-red-600/20 text-red-400 text-xs rounded hover:bg-red-600/30 transition-colors cursor-pointer"
                    title="Delete record"
                  >
                    🗑
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-400">
          <p>No conversions yet</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{backgroundColor: 'rgba(0, 0, 0, 0.3)'}}
          onClick={() => !isDeleting && handleCancelDelete()}
        >
          <div
            className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-white mb-4">⚠️ Delete Conversion?</h2>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete this conversion record? This action cannot be undone.
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors font-medium disabled:bg-gray-800 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500 transition-colors font-medium disabled:bg-red-800 disabled:cursor-not-allowed"
              >
                {isDeleting ? '⏳ Deleting...' : '🗑 Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
